import {
  deserify,
  FeatureGateName,
  getLocalStorage,
  getMediaBasePath,
  isFeatureGateEnabled,
  shouldIncludeCredentialsInRequest,
  useFeatureEnabled,
  type FeatureGateName as FeatureGateNameType,
  type FeatureGateOptions,
} from '@geneo2-web/shared-ui';
import { Feature } from '@protos/school_management/school.db_pb';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../reduxStore/reduxHooks';

export const useAuthentication = () => {
  const isAuthenticated = useMemo(() => {
    //check token in localStorage
    let auth = getLocalStorage('auth');
    if (auth === 'true' || auth === 'false') {
      auth = JSON.parse(auth);
    }
    return auth;
  }, []);
  // const [isAuthenticated, setAuth] = useState<'true' | 'false'>('false');
  // useEffect(() => {
  //   const auth = getLocalStorage('auth');
  //   setAuth(auth);
  // });
  // useEffect(() => {
  //   const handleStorageChange = (e: StorageEvent) => {
  //     if (e.key === 'auth') {
  //       setAuth(e.newValue ? 'true' : 'false');
  //     }
  //   };
  //   window.addEventListener('storage', handleStorageChange);
  //   return () => {
  //     window.removeEventListener('storage', handleStorageChange);
  //   };
  // }, []);

  return isAuthenticated;
};

export const useWhiteLabeledUrl = () => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const school_Id = user_info?.schoolDetails[0]?.schoolId;
  const logo_url = user_info?.schoolDetails[0]?.schoolWhiteLabelImageUrl;
  const featureInfo = user_info?.schoolDetails[0]?.featuresPlanInfo;
  const isWhiteLabelingFeature = useFeatureEnabled(
    featureInfo,
    Feature.WHITE_LABELING
  );
  // if (!isWhiteLabelingFeature || !logo_url || !school_Id) {
  //   return undefined;
  // }
  // if (logo_url.startsWith('http')) {
  //   return logo_url;
  // }
  // return getMediaBasePath(
  //   `/school/${school_Id}/logos${logo_url}`,
  //   `schoolnetCdnImagesBucket`
  // );
  const [validatedUrl, setValidatedUrl] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;

    const validateImage = async () => {
      if (!isWhiteLabelingFeature || !logo_url || !school_Id) {
        if (!cancelled) setValidatedUrl(undefined);
        return;
      }

      const finalUrl = logo_url.startsWith('http')
        ? logo_url
        : getMediaBasePath(
            `/school/${school_Id}/logos${logo_url}`,
            'schoolnetCdnImagesBucket'
          );

      const exists = await imageExists_2(finalUrl);

      console.log('Logo URL:', finalUrl);
      console.log('Image exists:', exists);

      if (!cancelled) {
        setValidatedUrl(exists ? finalUrl : undefined);
      }
    };

    validateImage();

    return () => {
      cancelled = true;
    };
  }, [logo_url, isWhiteLabelingFeature]);

  return validatedUrl;
};
const imageExists = async (url: string) => {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      credentials: shouldIncludeCredentialsInRequest(url)
        ? 'include'
        : 'same-origin',
    });
    return response.ok;
  } catch {
    return false;
  }
};

const imageExists_2 = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();

    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);

    img.src = url;
  });

const GIL_COURSES_BOOK_IDS = [
  10175, 10162, 10176, 10360, 10361, 10362, 10363, 10364, 10365, 10366, 10367,
  10368,
];

export const useAllowResourceDownload = (subjectId?: number) => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const teachClassSubjects = user_info?.teachClassSubjects || [];
  const learnSubjects = teachClassSubjects.map((val) => val.subjects).flat();
  if (!subjectId) {
    return true;
  }
  const learnSubject = (learnSubjects || []).find(
    (sub) => sub.subjectId === subjectId
  );
  if (!learnSubject) {
    return true;
  }
  if (GIL_COURSES_BOOK_IDS.includes(learnSubject.bookId)) {
    return false;
  }
  return true;
};

export const useTextBookQuestionEnabled = () => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  return user_info?.schoolDetails[0]?.schoolId === 639; // ellora school
  // return true;
};

export const useAttendanceFeatureEnabled = () => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const featureInfo = user_info?.schoolDetails[0]?.featuresPlanInfo;
  return useFeatureEnabled(featureInfo, Feature.ASSESSMENT_ATTENDANCE);
};

/** School feature flags for Teach (learn) and Assess flows on teacher home. */
export const useTeachAssessFlow = () => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const featureInfo = user_info?.schoolDetails[0]?.featuresPlanInfo;
  const isTeachFlowEnabled = useFeatureEnabled(featureInfo, Feature.TEACH_FLOW);
  const isAssessFlowEnabled = useFeatureEnabled(
    featureInfo,
    Feature.ASSESSMENT
  );
  const isAnalyticsEnabled = isAssessFlowEnabled || isTeachFlowEnabled;
  return { isTeachFlowEnabled, isAssessFlowEnabled, isAnalyticsEnabled };
};

function useTeacherSchoolId() {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  return user_info?.schoolDetails?.[0]?.schoolId;
}

/**
 * Named feature gate (school allowlist + optional feature-flag override).
 * @example useFeatureGate('webinarEnabled')
 * @example useFeatureGate('annotationsEnabled', { featureFlag: planFlag })
 */
export const useFeatureGate = (
  featureName: FeatureGateNameType,
  options?: Pick<FeatureGateOptions, 'featureFlag'>
) => {
  const schoolId = useTeacherSchoolId();
  return isFeatureGateEnabled(featureName, { schoolId, ...options });
};

export const useWebinarEnabled = (featureFlag?: boolean | null) =>
  useFeatureGate(FeatureGateName.webinarEnabled, { featureFlag });

export const useAnnotationsEnabled = (featureFlag?: boolean | null) =>
  useFeatureGate(FeatureGateName.annotationsEnabled, { featureFlag });

// Demo features, shown only for schools allowed by the shared feature gate.
export const useLearningHomeworkEnabled = () =>
  useFeatureGate(FeatureGateName.learningHomeworkEnabled);

export const useAllContentUploadsEnabled = () =>
  useFeatureGate(FeatureGateName.allContentUploadsEnabled);

/**
 * School feature flag for the Laboratory (project assessment) flow.
 * When disabled, project entry points are hidden across the app.
 */
export const useProjectAssessmentEnabled = () => {
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const featureInfo = user_info?.schoolDetails[0]?.featuresPlanInfo;
  return useFeatureEnabled(featureInfo, Feature.PROJECT_ASSESSMENT);
};
